import { Button, Result, Table } from 'antd'
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { AssignmentSections } from '../../../../../../constants/assignment-sections'
import {
    FLAGGED_PARAM,
    FOCUS_QUESTION_ID_PARAM,
    OPEN_SUBMISSION_ID_PARAM,
} from '../../../../../../constants/query-params'
import useQueryParamSetter from '../../../../../../hooks/useQueryParamSetter'
import { useFetchQuestionsQuery } from '../../../../../../store/apis/question.api'
import { useFetchEscalatedQuestionAnswersQuery } from '../../../../../../store/apis/submission.api'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import Routes from '../../../../../../constants/routes'
import './escalated-submissions-table.scss'
import { FlagOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { flatten } from '../../../../../../utils/array.helper'

export const escalatedStatuses = {
    manualUnresolved: 1,
    unexplainableUnresolved: 3,
}

const EscalatedSubmissionsTable = ({ filtersState }) => {
    const { token } = useParams()
    const versionId = useVersionId()
    const setQueryParam = useQueryParamSetter()
    const navigate = useNavigate()

    const { data: componentQuestions, isFetching: isQuestionsFetching } = useFetchQuestionsQuery({ token, versionId })

    const questions = useMemo(
        () => (componentQuestions ? flatten(componentQuestions, { excludeParents: true }) : null),
        [componentQuestions],
    )

    const {
        data: escalatedAnswersPerQuestion,
        isFetching,
        error,
        refetch,
    } = useFetchEscalatedQuestionAnswersQuery({ token, versionId })

    const onSelectSubmission = (submissionId, questionId) => {
        setQueryParam({
            [FLAGGED_PARAM]: 'true',
            [OPEN_SUBMISSION_ID_PARAM]: submissionId,
            [FOCUS_QUESTION_ID_PARAM]: questionId,
        })
        navigate({
            pathname: `${Routes.ASSIGNMENTS}/${token}/all/${AssignmentSections.HOME}`,
            search: location.search,
        })
    }

    const columns = [
        {
            title: 'Student id',
            dataIndex: 'studentId',
            key: 'studentId',
            render: (studentId, escalatedSubmission) => (
                <a
                    type='link'
                    onClick={() => onSelectSubmission(escalatedSubmission.submissionId, escalatedSubmission.questionId)}
                >
                    {studentId}
                </a>
            ),
            sorter: {
                compare: (a, b) => (a.studentId > b.studentId ? 1 : a.studentId < b.studentId ? -1 : 0),
                multiple: 1,
            },
            defaultSortOrder: 'ascend',
        },
        {
            title: 'Question',
            dataIndex: 'questionName',
            key: 'questionName',
            sorter: { compare: (a, b) => a.questionOrder - b.questionOrder, multiple: 2 },
            defaultSortOrder: 'ascend',
        },
        {
            title: 'Reviewer name',
            dataIndex: 'user',
            key: 'user',
            render: (user, escalatedSubmission) => (
                <div className='d-flex'>
                    <div>{user?.name ?? 'System'}</div>

                    <div style={{ paddingLeft: 8, paddingTop: 2 }}>
                        {escalatedSubmission.status === escalatedStatuses.manualUnresolved ? (
                            <FlagOutlined style={{ fontSize: 16 }} />
                        ) : (
                            <QuestionCircleOutlined style={{ fontSize: 16 }} />
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
        },
    ]

    const data = useMemo(() => {
        if (!escalatedAnswersPerQuestion) {
            return []
        }

        let allEscalatedAnswers = []

        for (const questionId of Object.keys(escalatedAnswersPerQuestion)) {
            const question = questions?.find(q => q.id == questionId)

            allEscalatedAnswers = allEscalatedAnswers.concat(
                escalatedAnswersPerQuestion[questionId].map(answer => ({
                    ...answer,
                    questionName: question?.name,
                    questionOrder: question?.order,
                })),
            )
        }

        const statusesToDisplay = Object.keys(filtersState)
            .filter(key => filtersState[key])
            .map(Number)

        if (statusesToDisplay.length) {
            allEscalatedAnswers = allEscalatedAnswers.filter(answer => statusesToDisplay.includes(answer.status))
        }

        return allEscalatedAnswers
    }, [escalatedAnswersPerQuestion, questions, filtersState])

    return error ? (
        <Result
            status='error'
            title='Escalated submissions fetch failed'
            subTitle={error}
            extra={[
                <Button key='try' onClick={() => refetch()} loading={isFetching || isQuestionsFetching}>
                    Try Again
                </Button>,
            ]}
        />
    ) : (
        <Table
            size='small'
            columns={columns}
            dataSource={data}
            pagination={data.length > 10 ? { perPage: 10 } : false}
            rowClassName={'test-class'}
            loading={{ spinning: isFetching || isQuestionsFetching, tip: 'Loading escalated submissions...' }}
        />
    )
}

export default EscalatedSubmissionsTable
